/**
 * ============================================================
 * Person.js — Public Individual Person Page
 * ============================================================
 *
 * Loads:
 *   -> Person info              (GET /api/people/:id)
 *   →->Person’s project list
 *   -> Person’s publications
 *
 * Backend returns:
 * {
 *   personData: { ... },        // person record
 *   projectData: [...],         // project membership records
 *   publicationData: [...]      // publications authored
 * }
 **/

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container } from "react-bootstrap";

import SynlabNavbar from "../components/SynlabNavbar";
import SynlabFooter from "../components/SynlabFooter";
import ProjectCardCompact from "../components/ProjectCardCompact";
import PubCardCompact from "../components/PubCardCompact";

import "./css/Page.css";     // global page layout
import "./css/Subpage.css";  // shared layout for subpages
import "./css/Person.css";   // page-specific styling

import { personPhoto } from "../utils/mediaPaths";
import { sortPubsByYearAndTitle } from "../utils/sortPubs";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function Person() {
  const { id } = useParams();

  const [person, setPerson] = useState(null);
  const [projects, setProjects] = useState([]);
  const [publications, setPublications] = useState([]);

  const [showMoreProjects, setShowMoreProjects] = useState(false);
  const [showMorePubs, setShowMorePubs] = useState(false);

  // ----------------------------------------------------------
  // Load Person + Projects + Publications
  // ----------------------------------------------------------
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`/api/people/${id}`);
        const data = await res.json();

        setPerson(data.personData || null);
        setProjects(data.projectData || []);
        setPublications(data.publicationData || []);

      } catch (err) {
        console.error("Failed to load person", err);
      }
    }

    load();
  }, [id]);

  if (!person) return null; // Prevents flash of undefined values

  // ----------------------------------------------------------
  // Get the photo path
  // ----------------------------------------------------------
  const photoSrc = personPhoto(person.photo);

  // ----------------------------------------------------------
  // Project Sorting & Grouping
  // ----------------------------------------------------------
  const current = projects
    .filter(p => !p.past)
    .sort((a, b) => a.name.localeCompare(b.name));

  const groupedPast = {};
  projects
    .filter(p => p.past)
    .sort((a, b) => {
      const yA = parseInt(a.endyear || 0, 10);
      const yB = parseInt(b.endyear || 0, 10);
      if (yA !== yB) return yB - yA; // newest first
      return a.name.localeCompare(b.name);
    })
    .forEach(p => {
      if (!groupedPast[p.endyear]) groupedPast[p.endyear] = [];
      groupedPast[p.endyear].push(p);
    });

  const pastYears = Object.keys(groupedPast).sort((a, b) => b - a);


  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <div className="page-wrapper">

      {/* ----- NAVBAR ----- */}
      <header className="header">
        <SynlabNavbar />
      </header>

      {/* ----- FLEX COLUMN WRAPPER ----- */}
      <div className="inner-page-wrapper">

        {/* -------- HEADER IMAGE (without title overlay) -------- */}
        <div className="header-image-container">
          <img
            src="/headers/Header_person.png"
            alt="Person Header"
            className="header-image"
          />
         {/* ----- <h1 className="header-title"></h1>----- */}
        </div>

        {/* -------- MAIN CONTENT -------- */}
        <section className="page-content">
          <Container className="page-inner" fluid="lg">

            {/* -------- NAME + TITLE -------- */}
            <div className="person-name-title-header">
              <h2 className="page-header">
                {person.firstname} {person.lastname}
              </h2>

              {person.title && person.title !== "null" && (
                <h3 className="page-subheader">{person.title}</h3>
              )}
            </div>

            {/* -------- PHOTO + BIO ROW -------- */}
            <div className="person-photo-bio-row">

              {/* ----- Photo ----- */}
              <div className="person-photo-wrapper">
                <img
                  className="person-photo"
                  src={photoSrc}
                  alt={`${person.firstname} ${person.lastname}`}
                />
              </div>

              {/* ----- Bio + Contact ----- */}
              <div className="person-bio-wrapper">
                <p className="person-bio-text">{person.bio}</p>

                {(person.email || person.url) && (
                  <div className="person-contact-inline">

                    {person.email && person.email !== "null" && (
                      <p className="person-contact-line">
                        <span className="person-contact-label-inline">Email:</span>
                        <span className="person-contact-value">{person.email}</span>
                      </p>
                    )}

                    {person.url && person.url !== "null" && (
                      <p className="person-contact-line">
                        <span className="person-contact-label-inline">Website:</span>
                        <a
                          href={person.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="person-contact-value link"
                        >
                          {person.url}
                        </a>
                      </p>
                    )}

                  </div>
                )}
              </div>

            </div>

            {/* -------- PROJECTS SECTION -------- */}
            {projects.length > 0 && (
              <div className="person-section">
                <h2 className="page-header">Projects</h2>

                {/* ----- Current Projects ----- */}
                {current.length > 0 && (
                  <div>
                    <h3 className="page-subheader">Current</h3>

                    <div className="page-container">
                      {current.map(project => (
                        <ProjectCardCompact key={project.id} project={project} />
                      ))}
                    </div>
                  </div>
                )}

                {/* ----- Past Projects ----- */}
                {pastYears.length > 0 && (
                  <div style={{ marginTop: "40px" }}>
                    <h3 className="page-subheader">Past</h3>

                    {pastYears
                      .slice(0, showMoreProjects ? pastYears.length : 1)
                      .map(year => (
                        <div key={year}>
                          <h4 className="page-subheader" style={{ color: "#707070" }}>
                            {year}
                          </h4>

                          <div className="page-container">
                            {groupedPast[year].map(project => (
                              <ProjectCardCompact key={project.id} project={project} />
                            ))}
                          </div>
                        </div>
                      ))}

                    {pastYears.length > 1 && (
                      <button
                        className="show-more-button"
                        onClick={() => setShowMoreProjects(!showMoreProjects)}
                      >
                        {showMoreProjects ? "Show Less" : "Show More"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* -------- Publications Section -------- */}
            {publications.length > 0 && (
              <div className="person-section">
                <h2 className="page-header">Publications</h2>

                  {(() => {
                    // Use sortPubs utility to sort pubs by year and title
                    const { flattened } = sortPubsByYearAndTitle(publications);

                    // Apply "show more" limit
                    const visible = showMorePubs ? flattened : flattened.slice(0, 5);

                    return (
                      <>
                        <div className="person-pubs-list">
                          {visible.map((pub, idx) => (
                            <PubCardCompact key={idx} publication={pub} />
                          ))}
                        </div>

                        {flattened.length > 5 && (
                          <button
                            className="show-more-button"
                            onClick={() => setShowMorePubs(!showMorePubs)}
                          >
                            {showMorePubs ? "Show Less" : "Show More"}
                          </button>
                        )}
                      </>
                    );
                  })()}

              </div>
            )}

          </Container>
        </section>
      </div>

      {/* ----- FOOTER ----- */}
      <SynlabFooter />
    </div>
  );
}

export default Person;
